import { inject } from '@angular/core';
import { PRIMARY_OUTLET, Router, type CanDeactivateFn } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SbDialogService } from '@sb/ui/dialog';
import { defer, map, of, switchMap, take, type Observable } from 'rxjs';

import { isPayrollFlowUrl } from './payroll-navigation';
import { PayrollProgressService } from './payroll-progress.service';
import { PayrollReportService } from './payroll-report.service';
import { PayrollRunService } from './payroll-run.service';

/**
 * Departures this guard must never hold back, keyed on the destination's first primary-outlet
 * segment.
 *
 * Signing out is the one that matters: it is a router navigation rather than a hard redirect
 * (`[routerLink]="['/logout']"` in the profile menu, and `UnauthorizedRedirectInterceptor` calls
 * `navigateByUrl('/logout')` on any authenticated 401). A confirm in front of it lets a manager
 * press Stay and keep a live session on a machine they believe they signed out of — and on the
 * 401 path it strands them on a dead session instead, with the payroll sources still polling and
 * every further 401 stacking another dialog.
 *
 * The rest are the forced redirects the account guards perform. None of them is a departure the
 * manager chose, so none of them is theirs to reconsider.
 */
const unvetoableDestinations = new Set(['logout', 'login', 'locked', 'account-closed', 'trial-expired', 'downgrade']);

/**
 * Confirms before a manager leaves a payroll run in progress. Nothing in the flow is persisted
 * and the page's destroy hook discards the run, so any departure throws the run away — which the
 * manager has to be able to back out of.
 *
 * A route guard rather than a handler on the flow's own Done button, because with the app's side
 * navigation present (SHB-24581) every nav item, the command palette and browser back are all
 * exits too. Done navigates like any of them and picks the confirm up here.
 *
 * The active step is asked first, before any of that: only the step knows about work held inside
 * it and saved nowhere — the Minijob step's typed payout hours are the first such thing — and the
 * passes below are all about the run's steps, not about what is typed into the one on screen. So a
 * step with something to lose is asked even when the run itself has nothing left to do. See
 * `PayrollRunService.canLeaveStep`.
 *
 * Departures that deliberately pass without asking (the step included):
 * - No scope. The header does not hold both a period and a department, so either no run was
 *   ever started or the manager emptied one of the two controls and discarded the one there
 *   was — nothing is left for a confirm to protect.
 * - Every non-report step resolved or skipped, which is what the panel's Done button means by
 *   "nothing left to lose"; leaving is then the natural end of the flow, not an interruption.
 * - A report already generated for this run. The manager reads it in the step itself, so leaving
 *   afterwards is the natural end of the flow rather than an interruption — and the thing the run
 *   existed to produce is saved in Reports, whatever the steps behind it still say.
 * - A destination that is still the payroll flow. Re-entering the flow it is already in
 *   discards nothing, so a warning about a discarded run would simply be untrue — see below.
 * - {@link unvetoableDestinations} — signing out, and the account guards' forced redirects.
 *
 * Losing the labs flag mid-session is handled differently again: the payroll page resets the run
 * before it redirects, so this guard sees no scope and stays out of the way on its own.
 */
export const payrollExitGuard: CanDeactivateFn<unknown> = (
  _component,
  _currentRoute,
  _currentState,
  nextState,
): Observable<boolean> => {
  const run = inject(PayrollRunService);
  const progress = inject(PayrollProgressService);
  const report = inject(PayrollReportService);
  // Injected up front rather than beside the confirm they build: the confirm is now reached from
  // inside a pipe, which runs outside the guard's injection context.
  const translate = inject(TranslateService);
  const dialogService = inject(SbDialogService);

  const destination = inject(Router).parseUrl(nextState.url).root.children[PRIMARY_OUTLET]?.segments[0]?.path;

  // Staying in the flow is not leaving it. Links into the flow carry different query params — a
  // period, departments, a step — so following another one mid-run is a real navigation the router
  // runs this guard for — but the page is reused, `onDestroy` never fires and `#run.reset()` never
  // runs, so the run the dialog offered to discard would still be there afterwards. Anything else
  // under `/payroll` is a departure like any other.
  if (isPayrollFlowUrl(nextState.url) || unvetoableDestinations.has(destination ?? '')) {
    return of(true);
  }

  // Before the step is asked: with no run there is no step on screen to have anything to lose.
  if (!run.hasScope()) {
    return of(true);
  }

  // Deferred, so the step is asked when the router subscribes rather than when the guard is built,
  // and a step whose check throws errors the navigation instead of the guard call itself.
  return defer(() => run.canLeaveStep()).pipe(
    switchMap((canLeaveStep) => {
      if (!canLeaveStep) {
        return of(false);
      }

      if (progress.nothingLeftToDo() || report.hasGeneratedReport()) {
        return of(true);
      }

      return dialogService
        .openConfirm({
          title: translate.instant('Exit payroll preparation?'),
          description: translate.instant(
            'Payroll steps are not finished yet. If you leave now, this run is discarded — next time you will need to set the period again.',
          ),
          type: 'warning',
        })
        .closed.pipe(
          take(1),
          map((result) => result?.confirmed === true),
        );
    }),
  );
};
